import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, forwardRef } from "react";
import { useRoadmap, taskHash } from "@/context/RoadmapContext";
import { TaskRow } from "./TaskRow";

const MOIS = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];
const JOURS = ["Dim","Lun","Mar","Mer","Jeu","Ven","Sam"];

function calculerPaques(a) {
  const aa=a%19,b=Math.floor(a/100),c=a%100,d=Math.floor(b/4),e=b%4,f=Math.floor((b+8)/25),g=Math.floor((b-f+1)/3);
  const h=(19*aa+b-d-g+15)%30,i=Math.floor(c/4),k=c%4,l=(32+2*e+2*i-h-k)%7,m=Math.floor((aa+11*h+22*l)/451);
  return new Date(a,Math.floor((h+l-7*m+114)/31)-1,((h+l-7*m+114)%31)+1);
}
function getJoursFeries(a) {
  const p=calculerPaques(a),v=new Date(p);v.setDate(p.getDate()-2);
  const l=new Date(p);l.setDate(p.getDate()+1);const asc=new Date(p);asc.setDate(p.getDate()+39);
  const pe=new Date(p);pe.setDate(p.getDate()+50);
  const j=new Date(a,8,1);let dw=j.getDay();let dd=(4-dw+7)%7;if(dw===0)dd+=7;else dd+=(7-dw+4);j.setDate(1+dd);
  return [new Date(a,0,1),new Date(a,11,25),new Date(a,11,31),v,l,asc,pe,j];
}

const mensuellesDef = [
  { dayRule: 10, text: "<strong class='text-gray-900 dark:text-white'>Déclaration AVS/AC/CAF & Paiement LPP</strong>" },
  { dayRule: "last", text: "<strong class='text-gray-900 dark:text-white'>Saisie heures BauBit Pro</strong>" },
  { dayRule: 25, text: "<strong class='text-gray-900 dark:text-white'>Validation salaires</strong>" },
  { dayRule: "first", text: "<strong class='text-gray-900 dark:text-white'>Virements salaires</strong>" },
];
const specifiquesDef = {
  0:[{dayRule:30,text:"<strong class='text-gray-900 dark:text-white'>Déclaration annuelle salaires AVS</strong>"},{dayRule:31,text:"<strong class='text-gray-900 dark:text-white'>Remise certificats impôt source</strong>"}],
  1:[{dayRule:"last",text:"<strong class='text-gray-900 dark:text-white'>Remise TVA T4 & paiement</strong>"}],
  3:[{dayRule:30,text:"<strong class='text-gray-900 dark:text-white'>Certificats LPP annuels. Déclaration ICC/IFD</strong>"}],
  4:[{dayRule:31,text:"<strong class='text-gray-900 dark:text-white'>Remise TVA T1 & paiement</strong>"}],
  5:[{dayRule:30,text:"<strong class='text-gray-900 dark:text-white'>Délai max AGO</strong>"}],
  7:[{dayRule:31,text:"<strong class='text-gray-900 dark:text-white'>Remise TVA T2 & paiement</strong>"}],
  8:[{dayRule:30,text:"<strong class='text-gray-900 dark:text-white'>Délai max (prolongation) déclaration fiscale</strong>"}],
  10:[{dayRule:30,text:"<strong class='text-gray-900 dark:text-white'>Remise TVA T3 & paiement</strong>"}],
};
const evenementielles = [
  "<strong class='text-gray-900 dark:text-white'>Inscription RC suite AG/CA</strong> (Sans délai)",
  "<strong class='text-gray-900 dark:text-white'>Paiement redevance radio-TV (Serafe)</strong> (Sur facturation)",
  "<strong class='text-gray-900 dark:text-white'>Déclaration accident de travail LAA</strong> (< 5 jours)",
  "<strong class='text-gray-900 dark:text-white'>Affiliation AVS nouvel employé</strong> (< 10 jours)",
  "<strong class='text-gray-900 dark:text-white'>Envoi convocations avant AG</strong> (- 20 jours)",
  "<strong class='text-gray-900 dark:text-white'>Paiement IFD, mutation, solde compte</strong> (< 30 jours)",
  "<strong class='text-gray-900 dark:text-white'>Paiement impôt anticipé dividendes</strong> (< 30 jours post-échéance)",
  "<strong class='text-gray-900 dark:text-white'>Affiliation LPP nouvel employé</strong> (< 3 mois)",
];

function generateMonths(startYear, startMonth, count) {
  const result = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(startYear, startMonth + i);
    result.push({ year: d.getFullYear(), month: d.getMonth(), key: `${d.getFullYear()}-${d.getMonth()}` });
  }
  return result;
}

export default function CalendarView() {
  const today = useMemo(() => new Date(), []);
  const curYear = today.getFullYear();
  const curMonth = today.getMonth();
  const curDay = today.getDate();

  const [displayYear, setDisplayYear] = useState(curYear);
  const [vacations, setVacations] = useState([]);
  const [startVal, setStartVal] = useState("");
  const [endVal, setEndVal] = useState("");
  const scrollRef = useRef(null);
  const curMonthRef = useRef(null);
  const todayRef = useRef(null);
  const initialPositioningDoneRef = useRef(false);
  const prependRef = useRef(null);
  const loadingRef = useRef(false);
  const rafRef = useRef(null);

  // Align curMonth at index 16: 16%3=1 (2nd in 3-col), 16%2=0 (left in 2-col)
  const [months, setMonths] = useState(() => {
    const start = new Date(curYear, curMonth - 16);
    return generateMonths(start.getFullYear(), start.getMonth(), 40);
  });

  const feriesMap = useMemo(() => {
    const map = {};
    const years = new Set(months.map(m => m.year));
    years.forEach(y => { map[y] = getJoursFeries(y); });
    return map;
  }, [months]);

  const isDayOff = useCallback((date) => {
    const y = date.getFullYear();
    const feries = feriesMap[y] || [];
    const isW = date.getDay() === 0 || date.getDay() === 6;
    const isF = feries.some(f => f.getDate() === date.getDate() && f.getMonth() === date.getMonth());
    const isV = vacations.some(p => date.getTime() >= p.start && date.getTime() <= p.end);
    return isW || isF || isV;
  }, [feriesMap, vacations]);

  const getDernierJourOuvre = useCallback((dateTarget) => {
    let d = new Date(dateTarget);
    let i = 0;
    while (isDayOff(d) && i < 10) { d.setDate(d.getDate() - 1); i++; }
    return d;
  }, [isDayOff]);

  const getPremierJourOuvre = useCallback((dateTarget) => {
    let d = new Date(dateTarget);
    let i = 0;
    while (isDayOff(d) && i < 10) { d.setDate(d.getDate() + 1); i++; }
    return d;
  }, [isDayOff]);

  const scrollElementBelowHeader = useCallback((target, behavior = "smooth", extraOffset = 18) => {
    const container = scrollRef.current;
    if (!container || !target) return;
    const containerRect = container.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    container.scrollTo({
      top: Math.max(0, container.scrollTop + (targetRect.top - containerRect.top) - extraOffset),
      behavior,
    });
  }, []);

  // Scroll handler: detect year + extend range
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const handler = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        if (loadingRef.current) return;

        const containerRect = el.getBoundingClientRect();
        const cards = el.querySelectorAll('[data-month-key]');
        const visibleYears = new Set();
        for (const card of cards) {
          const rect = card.getBoundingClientRect();
          const visibleTop = Math.max(rect.top, containerRect.top);
          const visibleBottom = Math.min(rect.bottom, containerRect.bottom);
          if (visibleBottom - visibleTop <= 0) continue;
          visibleYears.add(parseInt(card.dataset.monthKey.split('-')[0], 10));
        }
        if (visibleYears.size > 0) {
          const dominantYear = Math.max(...Array.from(visibleYears));
          setDisplayYear(prev => (prev !== dominantYear ? dominantYear : prev));
        }

        // Auto-prepend when scrolling near the top
        if (el.scrollTop < 300) {
          loadingRef.current = true;
          prependRef.current = el.scrollHeight;
          setMonths(prev => {
            const first = prev[0];
            const start = new Date(first.year, first.month - 12);
            return [...generateMonths(start.getFullYear(), start.getMonth(), 12), ...prev];
          });
        }

        // Auto-append: check if last month card is near the viewport
        if (cards.length > 0) {
          const lastCard = cards[cards.length - 1];
          const lastRect = lastCard.getBoundingClientRect();
          if (lastRect.top < containerRect.bottom + 500) {
            setMonths(prev => {
              const last = prev[prev.length - 1];
              const start = new Date(last.year, last.month + 1);
              return [...prev, ...generateMonths(start.getFullYear(), start.getMonth(), 12)];
            });
          }
        }
      });
    };

    el.addEventListener('scroll', handler, { passive: true });
    return () => {
      el.removeEventListener('scroll', handler);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // Fix scroll position after prepend
  useLayoutEffect(() => {
    if (prependRef.current && scrollRef.current) {
      const diff = scrollRef.current.scrollHeight - prependRef.current;
      scrollRef.current.scrollTop += diff;
      prependRef.current = null;
    }
    loadingRef.current = false;
  }, [months]);

  // Scroll to current month on mount
  useEffect(() => {
    if (initialPositioningDoneRef.current) return;
    const retries = [150, 450, 900];
    const timers = retries.map((delay, index) => setTimeout(() => {
      if (!curMonthRef.current) return;
      scrollElementBelowHeader(curMonthRef.current, index === 0 ? "auto" : "smooth", 18);
      if (index === retries.length - 1) initialPositioningDoneRef.current = true;
    }, delay));
    return () => timers.forEach(clearTimeout);
  }, [scrollElementBelowHeader]);

  // Year navigation
  const navigateYear = useCallback((delta) => {
    const targetYear = displayYear + delta;
    const targetKey = `${targetYear}-0`;
    const existing = scrollRef.current?.querySelector(`[data-month-key="${targetKey}"]`);
    if (existing) {
      scrollElementBelowHeader(existing, 'smooth', 18);
    } else {
      // Extend months to include target year
      if (delta < 0) {
        prependRef.current = scrollRef.current?.scrollHeight;
        setMonths(prev => {
          const firstYear = prev[0].year;
          if (targetYear >= firstYear) return prev;
          const first = prev[0];
          const count = (first.year - targetYear) * 12 + first.month;
          const start = new Date(first.year, first.month - count);
          return [...generateMonths(start.getFullYear(), start.getMonth(), count), ...prev];
        });
      } else {
        setMonths(prev => {
          const lastYear = prev[prev.length - 1].year;
          if (targetYear <= lastYear) return prev;
          const last = prev[prev.length - 1];
          const count = (targetYear - last.year) * 12 + (11 - last.month);
          const start = new Date(last.year, last.month + 1);
          return [...prev, ...generateMonths(start.getFullYear(), start.getMonth(), count)];
        });
      }
      setTimeout(() => {
        const target = scrollRef.current?.querySelector(`[data-month-key="${targetKey}"]`);
        if (target) scrollElementBelowHeader(target, 'smooth', 18);
      }, 200);
    }
  }, [displayYear, scrollElementBelowHeader]);

  const scrollTodayIntoView = useCallback(() => {
    scrollElementBelowHeader(todayRef.current, 'smooth', 18);
  }, [scrollElementBelowHeader]);

  const addVacation = () => {
    const re = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[012])$/;
    if (re.test(startVal) && re.test(endVal)) {
      const [sD, sM] = startVal.split("/").map(Number);
      const [eD, eM] = endVal.split("/").map(Number);
      const start = new Date(displayYear, sM - 1, sD).getTime();
      let end = new Date(displayYear, eM - 1, eD).getTime();
      if (end < start) end = new Date(displayYear + 1, eM - 1, eD).getTime();
      setVacations(v => [...v, { start, end, label: `${startVal} - ${endVal}` }]);
      setStartVal(""); setEndVal("");
    }
  };

  return (
    <div className="flex flex-col h-full relative px-3 sm:px-6 pt-2 sm:pt-3" data-testid="calendar-view">
      {/* Controls */}
      <div className="shrink-0 mb-3 flex flex-wrap items-center gap-x-5 gap-y-2">
        <h2 className="text-lg sm:text-xl font-black text-gray-900 dark:text-white whitespace-nowrap">Planification Annuelle</h2>

        <div className="grid grid-cols-[2rem_minmax(4.5rem,auto)_2rem] items-center justify-items-center gap-2">
          <button onClick={() => navigateYear(-1)} className="flex h-8 w-8 items-center justify-center bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 rounded-full text-gray-600 dark:text-white" data-testid="btn-prev-year">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          </button>
          <span className="text-xl sm:text-2xl font-black text-[#4c7e55] dark:text-[#2e6d3b] tabular-nums min-w-[4.5rem] text-center" data-testid="year-display">{displayYear}</span>
          <button onClick={() => navigateYear(1)} className="flex h-8 w-8 items-center justify-center bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 rounded-full text-gray-600 dark:text-white" data-testid="btn-next-year">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
          </button>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider mr-1">Vacances</span>
          <input type="text" value={startVal} onChange={e => setStartVal(e.target.value)} placeholder={"D\u00e9but (ex\u00a0: 15/07)"} className="h-[38px] bg-gray-50 dark:bg-[#242424] border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white text-sm rounded-md px-2.5 py-1.5 w-[8.875rem]" data-testid="vacation-start" />
          <span className="text-gray-400 text-sm">{'\u2013'}</span>
          <input type="text" value={endVal} onChange={e => setEndVal(e.target.value)} placeholder={"Fin (ex\u00a0: 15/08)"} className="h-[38px] bg-gray-50 dark:bg-[#242424] border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white text-sm rounded-md px-2.5 py-1.5 w-[8.875rem]" data-testid="vacation-end" />
          <button onClick={addVacation} className="inline-flex h-[38px] items-center bg-[#4c7e55] hover:bg-[#416b49] dark:bg-[#2e6d3b] dark:hover:bg-[#255731] text-white text-xs font-bold px-3 rounded-md" data-testid="add-vacation-btn">Ajouter</button>
          <button onClick={scrollTodayIntoView} className="inline-flex h-[38px] w-[38px] items-center justify-center rounded-md border border-gray-300 bg-gray-50 text-[#4c7e55] transition-colors hover:bg-gray-100 dark:border-gray-700 dark:bg-[#242424] dark:text-[#2e6d3b] dark:hover:bg-[#2a2a2a]" data-testid="scroll-today-btn" title="Aller à aujourd’hui">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10m-11 9h12a2 2 0 002-2V7a2 2 0 00-2-2H6a2 2 0 00-2 2v11a2 2 0 002 2z" /></svg>
          </button>
        </div>

        {vacations.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {vacations.map((vp, i) => (
              <span key={i} className="bg-[#eef4ef] dark:bg-[rgba(46,109,59,0.22)] text-[#4c7e55] dark:text-[#4d8a5b] text-xs px-2 py-0.5 rounded flex items-center gap-1 border border-[#bfd1c3] dark:border-[#2e6d3b]">
                {vp.label}
                <button onClick={() => setVacations(v => v.filter((_, j) => j !== i))} className="text-[#4c7e55] dark:text-[#2e6d3b] hover:opacity-80 ml-0.5">&times;</button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Calendar Grid — Infinite Scroll */}
      <div ref={scrollRef} className="relative flex-1 overflow-y-auto calendar-scrollbar pr-2 sm:pr-4 -mr-2 sm:-mr-4 pb-10" style={{ scrollBehavior: "smooth" }} data-testid="calendar-scroll-area">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 pt-4">
          {months.map((m) => (
            <MonthCard
              key={m.key}
              ref={m.year === curYear && m.month === curMonth ? curMonthRef : null}
              month={m.month} year={m.year}
              feries={feriesMap[m.year] || []}
              curYear={curYear} curMonth={curMonth} curDay={curDay}
              vacations={vacations} isDayOff={isDayOff}
              getDernierJourOuvre={getDernierJourOuvre}
              getPremierJourOuvre={getPremierJourOuvre}
              isCurMonth={m.year === curYear && m.month === curMonth}
              monthKey={m.key}
              todayRef={todayRef}
            />
          ))}
        </div>
        {/* Events */}
        <div className="pt-4 pb-12">
          <div className="bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800 rounded-xl p-8 shadow-lg">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 border-b border-gray-200 dark:border-gray-800 pb-2">Obligations Événementielles (Sans date fixe)</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {evenementielles.map((evt, i) => {
                const tid = taskHash(evt + "-evt-" + i);
                return (
                  <div key={i} className="bg-gray-50 dark:bg-[#242424] border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <TaskRow taskId={tid} label={evt.replace(/<[^>]*>/g, "").substring(0, 200)} tab="Calendrier Interactif">
                      <span dangerouslySetInnerHTML={{ __html: evt }} />
                    </TaskRow>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const MonthCard = forwardRef(function MonthCard({ month: m, year, feries, curYear, curMonth, curDay, vacations, isDayOff, getDernierJourOuvre, getPremierJourOuvre, isCurMonth, monthKey, todayRef }, ref) {
  const isCur = year === curYear && m === curMonth;
  const daysInMonth = new Date(year, m + 1, 0).getDate();

  const monthTasks = useMemo(() => {
    const obj = {};
    const process = (r) => {
      let actual;
      if (r.dayRule === "last") {
        actual = getDernierJourOuvre(new Date(year, m, daysInMonth));
      } else if (r.dayRule === "first") {
        actual = getPremierJourOuvre(new Date(year, m, 1));
      } else {
        actual = getDernierJourOuvre(new Date(year, m, Math.min(r.dayRule, daysInMonth)));
      }
      let d = actual.getDate();
      if (!obj[d]) obj[d] = [];
      obj[d].push(r.text);
    };
    mensuellesDef.forEach(process);
    if (specifiquesDef[m]) specifiquesDef[m].forEach(process);
    return obj;
  }, [m, year, daysInMonth, getDernierJourOuvre, getPremierJourOuvre]);

  return (
    <div ref={ref} data-month-key={monthKey}
      className={`calendar-month-card bg-white dark:bg-[#242424] border border-gray-200 dark:border-gray-700 rounded-xl flex flex-col shadow-sm${isCurMonth ? ' calendar-current-month' : ''}`}
      data-testid={`month-card-${m}`}>
      {isCur && (
        <div className="absolute left-1/2 -translate-x-1/2 bg-red-500 text-white text-[10px] font-black px-3.5 py-1 rounded-full shadow-lg whitespace-nowrap tracking-wide" style={{ top: '-12px', zIndex: 110 }} data-testid="current-month-badge">
          MOIS EN COURS
        </div>
      )}
      <div className="calendar-month-header bg-white dark:bg-[#242424] px-3 sm:px-6 pt-4 pb-2 rounded-t-xl border-b border-gray-200 dark:border-gray-700">
        <h3 className={`text-base sm:text-lg font-bold uppercase tracking-widest text-center ${isCur ? "text-red-600 dark:text-red-500" : "text-[#4c7e55] dark:text-[#2e6d3b]"}`}>
          {MOIS[m]}
        </h3>
      </div>
      <div className="calendar-month-days flex flex-col gap-1 w-full p-3 sm:p-6 pt-4">
        {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((d) => (
          <DayEntry key={d} day={d} month={m} year={year} feries={feries}
            curYear={curYear} curMonth={curMonth} curDay={curDay}
            vacations={vacations} tasks={monthTasks[d]} dayRef={isCur && d === curDay ? todayRef : null} />
        ))}
      </div>
    </div>
  );
});

function DayEntry({ day: d, month: m, year, feries, curYear, curMonth, curDay, vacations, tasks, dayRef }) {
  const date = new Date(year, m, d);
  const dStr = JOURS[date.getDay()];
  const isF = feries.some(f => f.getDate() === d && f.getMonth() === m);
  const isW = date.getDay() === 0 || date.getDay() === 6;
  const isT = year === curYear && m === curMonth && d === curDay;
  const isV = vacations.some(p => date.getTime() >= p.start && date.getTime() <= p.end);
  const hasTasks = tasks && tasks.length > 0;

  let cls = "flex p-2 rounded-lg border items-start gap-3 ";
  let stl = "text-gray-500 dark:text-gray-400 font-medium w-14 shrink-0 text-right";
  let dwCls = "text-[11px] uppercase opacity-60 leading-none";
  let tagCls = "text-[11px] uppercase leading-none font-bold mt-[1px]";

  if (isT) { cls += "is-today border-transparent "; stl = "text-green-600 dark:text-green-400 font-black w-14 shrink-0 text-right"; dwCls = "text-[11px] uppercase opacity-100 font-bold leading-none"; }
  else if (isF) { cls += "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-900/50 "; stl = "text-red-600 dark:text-red-400 font-bold w-14 shrink-0 text-right"; }
  else if (isV) { cls += "bg-[#eef4ef] dark:bg-[rgba(46,109,59,0.18)] border-[#bfd1c3] dark:border-[#2e6d3b] "; stl = "text-[#4c7e55] dark:text-[#2e6d3b] font-bold w-14 shrink-0 text-right"; }
  else if (isW) { cls += "bg-gray-50 dark:bg-[#242424] border-gray-200 dark:border-gray-700 "; stl = "text-[#6b7d6e] dark:text-[#8ea391] font-medium w-14 shrink-0 text-right"; }
  else { cls += "hover:bg-gray-100 dark:hover:bg-gray-800/50 border-transparent "; }

  if (hasTasks && !isF && !isW && !isV && !isT) cls += "bg-[#eef4ef] dark:bg-[rgba(46,109,59,0.18)] border-[#bfd1c3] dark:border-[#2e6d3b] ";

  return (
    <div className={cls} ref={dayRef} data-testid={isT ? "calendar-today-entry" : undefined}>
      <div className={stl}>
        <div className={dwCls}>{dStr}</div>
        <div className="text-lg leading-none mt-1">{d}</div>
      </div>
      <div className="flex-grow flex flex-col justify-start">
        {isT && <div className={`${tagCls} text-green-600 dark:text-green-400 tracking-wider mb-1.5 font-black`}>Aujourd&apos;hui</div>}
        {isF && <div className={`${tagCls} text-red-600 dark:text-red-500 mb-1.5`}>Jour Férié</div>}
        {isV && <div className={`${tagCls} text-[#4c7e55] dark:text-[#2e6d3b] mb-1.5`}>Vacances</div>}
        {hasTasks ? tasks.map((t, ti) => {
          const tid = taskHash(t + "-cal-" + m + "-" + d + "-" + ti);
          return (
            <TaskRow key={ti} taskId={tid} label={t.replace(/<[^>]*>/g, "").substring(0, 200)} tab="Calendrier Interactif" className="mb-1 last:mb-0">
              <span dangerouslySetInnerHTML={{ __html: t }} />
            </TaskRow>
          );
        }) : <div className="text-xs text-gray-400 dark:text-gray-600">-</div>}
      </div>
    </div>
  );
}
